import AppStore from '@v4/store/appStore'
import { useCallback, useEffect, useState } from 'react'
import { POPUP_VARIANTS } from '@v4/components/sections/Popup'
import { useSectionId } from '@v4/components/common/SectionItem'
import PageBuilderSelector from '@v4/utils/selectors/PageBuilderSelector'
import usePopupButton from '@v4/hooks/usePopupButton'
import useFieldFocus from '@v4/hooks/useFieldFocus'
import useEditorMeta from '@v4/hooks/useEditorMeta'

export const useChangeEmail = ({ metaData }) => {
	const sectionId = useSectionId()
	const activeSectionId = PageBuilderSelector.useActiveSectionId()
	const { setActiveSectionId } = PageBuilderSelector.useActions()
	const isBuilderMode = AppStore.useStoreState(state => state.general.isBuilderMode)
	const signUpRequired = AppStore.useStoreState(state => state.result.data.sing_up_required)
	const [showMessage, setShowMessage] = useState(false)

	const { handleClick } = usePopupButton(signUpRequired ? POPUP_VARIANTS.SING_UP_RESULT : POPUP_VARIANTS.CHANGE_EMAIL)

	const handleDocumentClick = useCallback(
		event => {
			if (isBuilderMode || activeSectionId) return
			const { toggle, target } = event.target.dataset

			if (toggle === 'modal' && target === '#changeEmail') {
				handleClick(event)
			}
		},
		[activeSectionId, handleClick, isBuilderMode],
	)

	const handleClose = useCallback(
		event => {
			event.preventDefault()
			event.stopPropagation()
			setActiveSectionId({ id: null })
		},
		[setActiveSectionId],
	)

	const handleSuccess = useCallback(() => {
		setActiveSectionId({ id: null })
		setShowMessage(true)
	}, [setActiveSectionId])

	const handleCloseMessage = useCallback(() => {
		setShowMessage(false)
	}, [])

	const { handleFocus: handleTitleClick } = useFieldFocus({ metaData: metaData.title })
	const { handleFocus: handleButtonClick } = useFieldFocus({ metaData: metaData.buttonText })

	useEffect(() => {
		document.addEventListener('click', handleDocumentClick, true)
		return () => {
			document.removeEventListener('click', handleDocumentClick)
		}
	}, [handleDocumentClick])

	const { editable: titleEditable, handleChange: handleTitleChange } = useEditorMeta({
		metaData: metaData.title,
		key: 'title',
	})

	const { editable: buttonTextEditable, handleChange: handleButtonTextChange } = useEditorMeta({
		metaData: metaData.buttonText,
		key: 'buttonText',
	})

	return [
		{
			open: sectionId === activeSectionId,
			placeholderColor: metaData.placeholderColor?.metaValue,
			buttonText: metaData.buttonText?.metaValue,
			title: metaData.title?.metaValue,
			titleConfig: metaData.title?.config,
			showMessage,
			titleEditable,
			buttonTextEditable,
			buttonTextConfig: metaData.buttonText?.config,
			titleKey: metaData.title?.tierKey,
			buttonTextKey: metaData.buttonText?.tierKey,
		},
		{
			handleClose,
			handleSuccess,
			handleCloseMessage,
			handleTitleClick,
			handleButtonClick,
			handleTitleChange,
			handleButtonTextChange,
		},
	]
}
